import { useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";

const SELECT = "SELECT";
const FORM_NAME = "NPCI_MAPPING";
const PDF_ENDPOINT = "/FillMyForm/NPCI_MAPPING/Print.php";

const fields = [
  {
    name: "branch",
    label: "Branch",
    type: "text",
    maxLength: 15,
    required: true,
  },
  {
    name: "bank",
    label: "Bank Name",
    type: "text",
    maxLength: 30,
    required: true,
  },
  {
    name: "acno",
    label: "Account Number",
    type: "text",
    maxLength: 11,
    numeric: true,
    required: true,
  },
  {
    name: "name",
    label: "Applicant Name",
    type: "text",
    maxLength: 40,
    alphaOnly: true,
    required: true,
  },
  {
    name: "mobile",
    label: "Mobile Number",
    type: "text",
    maxLength: 10,
    numeric: true,
    required: true,
  },
  {
    name: "type",
    label: "Seeding Type",
    type: "select",
    required: true,
    options: [
      ["FRESH", "FRESH SEEDING"],
      [
        "MOVEMENT",
        "MOVEMENT - ONE BANK TO ANOTHER BANK",
      ],
    ],
  },
  {
    name: "embank",
    label: "Existing Mapped Bank",
    type: "text",
    maxLength: 40,
    required: true,
  },
];

const tabs = ["Applicant Details", "Preview"];

const initialFormData = Object.fromEntries(
  fields.map((field) => [
    field.name,
    field.type === "select" ? SELECT : "",
  ]),
);

async function readJson(response) {
  const text = await response.text();

  if (!text.trim()) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(
      "The server returned an invalid JSON response.",
    );
  }
}

function getOptionLabel(field, value) {
  if (
    !field.options ||
    !value ||
    value === SELECT
  ) {
    return "-";
  }

  return (
    field.options.find(
      ([optionValue]) =>
        optionValue === value,
    )?.[1] || value
  );
}

export default function NPCI_MAPPING() {
  const [formData, setFormData] =
    useState(initialFormData);

  const [errors, setErrors] = useState({});
  const [currentTab, setCurrentTab] =
    useState(0);

  const [maxAllowedTab, setMaxAllowedTab] =
    useState(0);

  const [pdfUrl, setPdfUrl] =
    useState("");

  const [showPdf, setShowPdf] =
    useState(false);

  const [pdfLoading, setPdfLoading] =
    useState(false);

  const [pdfError, setPdfError] =
    useState("");

  const nextButtonRef = useRef(null);

  const isMovement =
    formData.type === "MOVEMENT";

  const isFieldDisabled = (field) =>
    field.name === "embank" &&
    !isMovement;

  const enabledFields = fields.filter(
    (field) => !isFieldDisabled(field),
  );

  useEffect(() => {
    if (currentTab !== 0) {
      return undefined;
    }

    const frame =
      window.requestAnimationFrame(() => {
        document
          .getElementById("branch")
          ?.focus();
      });

    return () =>
      window.cancelAnimationFrame(frame);
  }, [currentTab]);

  useEffect(() => {
    if (!isMovement) {
      setFormData((previous) => ({
        ...previous,
        embank: "",
      }));

      setErrors((previous) => ({
        ...previous,
        embank: "",
      }));
    }
  }, [isMovement]);

  useEffect(() => {
    const controller =
      new AbortController();

    (async () => {
      try {
        const response = await fetch(
          `/backend/api/autofill.php?form=${encodeURIComponent(
            FORM_NAME,
          )}`,
          {
            credentials: "include",
            cache: "no-store",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          },
        );

        const result =
          await readJson(response);

        if (
          !response.ok ||
          !result?.success ||
          !result?.found ||
          !result?.data
        ) {
          return;
        }

        const data = result.data;

        setFormData((previous) => ({
          ...previous,
          branch:
            previous.branch ||
            data.branch ||
            "",
        }));
      } catch (error) {
        if (
          error.name !== "AbortError"
        ) {
          console.error(
            "NPCI_MAPPING autofill error:",
            error,
          );
        }
      }
    })();

    return () => controller.abort();
  }, []);

  useEffect(
    () => () => {
      if (
        pdfUrl?.startsWith("blob:")
      ) {
        URL.revokeObjectURL(pdfUrl);
      }
    },
    [pdfUrl],
  );

  const updateField = (
    field,
    rawValue,
  ) => {
    let value = rawValue;

    if (field.numeric) {
      value = value.replace(/\D/g, "");
    }

    if (field.alphaOnly) {
      value = value.replace(/[^A-Za-z ]/g, "");
    }

    setFormData((previous) => ({
      ...previous,
      [field.name]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [field.name]: "",
    }));
  };

  const validateField = (field) => {
    if (isFieldDisabled(field)) {
      return "";
    }

    const value = String(
      formData[field.name] ?? "",
    ).trim();

    if (
      field.required &&
      (field.type === "select"
        ? !value || value === SELECT
        : !value)
    ) {
      return `${field.label} is required.`;
    }

    if (
      field.alphaOnly &&
      value &&
      !/^[A-Za-z ]+$/.test(value)
    ) {
      return `${field.label} must contain alphabet letters only.`;
    }

    if (
      field.name === "acno" &&
      value &&
      !/^\d{11}$/.test(value)
    ) {
      return "Account number must contain 11 digits.";
    }

    if (
      field.name === "mobile" &&
      value &&
      !/^\d{10}$/.test(value)
    ) {
      return "Mobile number must contain 10 digits.";
    }

    return "";
  };

  const validateAll = () => {
    const nextErrors = {};

    fields.forEach((field) => {
      const message =
        validateField(field);

      if (message) {
        nextErrors[field.name] =
          message;
      }
    });

    setErrors(nextErrors);

    return (
      Object.keys(nextErrors)
        .length === 0
    );
  };

  const goNext = () => {
    if (!validateAll()) {
      return;
    }

    setCurrentTab(1);
    setMaxAllowedTab(1);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const goPrevious = () => {
    setCurrentTab(0);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleLastFieldKeyDown = (
    event,
    fieldName,
  ) => {
    const lastFieldName =
      enabledFields[
        enabledFields.length - 1
      ]?.name;

    if (
      fieldName === lastFieldName &&
      event.key === "Tab" &&
      !event.shiftKey
    ) {
      event.preventDefault();

      nextButtonRef.current?.focus();
    }
  };

  const handleSubmit = async (
    event,
  ) => {
    event.preventDefault();

    if (!validateAll()) {
      setCurrentTab(0);
      return;
    }

    setPdfLoading(true);
    setPdfError("");

    try {
      const body = new FormData();

      Object.entries(formData).forEach(
        ([name, value]) => {
          body.append(
            name,
            value === SELECT
              ? ""
              : String(value ?? ""),
          );
        },
      );

      const controller =
        new AbortController();

      const timeoutId =
        window.setTimeout(
          () =>
            controller.abort(),
          60000,
        );

      let response;

      try {
        response = await fetch(
          PDF_ENDPOINT,
          {
            method: "POST",
            body,
            credentials: "include",
            headers: {
              Accept:
                "application/pdf",
            },
            signal:
              controller.signal,
          },
        );
      } finally {
        window.clearTimeout(
          timeoutId,
        );
      }

      if (!response.ok) {
        throw new Error(
          (await response.text()) ||
            `PDF generation failed (${response.status}).`,
        );
      }

      const contentType =
        response.headers.get(
          "content-type",
        ) || "";

      if (
        !contentType.includes(
          "application/pdf",
        )
      ) {
        throw new Error(
          (await response.text()) ||
            "Print.php did not return a PDF file.",
        );
      }

      const objectUrl =
        URL.createObjectURL(
          await response.blob(),
        );

      setPdfUrl((previous) => {
        if (
          previous?.startsWith(
            "blob:",
          )
        ) {
          URL.revokeObjectURL(
            previous,
          );
        }

        return objectUrl;
      });

      setShowPdf(true);
    } catch (error) {
      setPdfError(
        error.name === "AbortError"
          ? "PDF generation timed out."
          : error.message ||
              "Unable to generate PDF preview.",
      );
    } finally {
      setPdfLoading(false);
    }
  };

  const renderField = (field) => {
    const disabled =
      isFieldDisabled(field);

    const error =
      errors[field.name];

    return (
      <div
        key={field.name}
        className="space-y-2"
      >
        <Label htmlFor={field.name}>
          {field.label}

          {field.required &&
            !disabled && (
              <span className="text-red-500">
                {" "}
                *
              </span>
            )}
        </Label>

        {field.type === "select" ? (
          <select
            id={field.name}
            name={field.name}
            value={
              formData[field.name]
            }
            disabled={disabled}
            onChange={(event) =>
              updateField(
                field,
                event.target.value,
              )
            }
            onKeyDown={(event) =>
              handleLastFieldKeyDown(
                event,
                field.name,
              )
            }
            className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50 ${
              error
                ? "border-red-500"
                : ""
            }`}
          >
            <option value={SELECT}>
              SELECT
            </option>

            {field.options.map(
              ([value, label]) => (
                <option
                  key={`${field.name}-${value}`}
                  value={value}
                >
                  {label}
                </option>
              ),
            )}
          </select>
        ) : (
          <Input
            id={field.name}
            name={field.name}
            type="text"
            value={
              formData[field.name]
            }
            maxLength={
              field.maxLength
            }
            disabled={disabled}
            placeholder={field.label}
            className={
              error
                ? "border-red-500"
                : ""
            }
            onChange={(event) =>
              updateField(
                field,
                event.target.value,
              )
            }
            onKeyDown={(event) =>
              handleLastFieldKeyDown(
                event,
                field.name,
              )
            }
          />
        )}

        {field.name === "embank" &&
          disabled && (
            <p className="text-sm text-muted-foreground">
              Required only for movement
              from one bank to another.
            </p>
          )}

        {error && (
          <p className="text-sm text-red-500">
            {error}
          </p>
        )}
      </div>
    );
  };

  if (showPdf && pdfUrl) {
    return (
      <PdfViewerContainer
        pdfUrl={pdfUrl}
        title="NPCI Mapping Form"
        onBack={() =>
          setShowPdf(false)
        }
      />
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-7xl">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-3xl">
              🏦 NPCI Mapping Form
            </CardTitle>

            <p className="text-muted-foreground">
              Enter the bank, account, applicant,
              mobile, and seeding details carefully.
            </p>
          </CardHeader>
        </Card>

        <div className="mb-8 grid grid-cols-2 gap-3">
          {tabs.map(
            (tab, index) => (
              <Button
                key={tab}
                type="button"
                variant={
                  currentTab === index
                    ? "default"
                    : "outline"
                }
                disabled={
                  index >
                  maxAllowedTab
                }
                onClick={() =>
                  setCurrentTab(index)
                }
              >
                {index + 1}. {tab}
              </Button>
            ),
          )}
        </div>

        {currentTab === 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>
                👤 Applicant Details
              </CardTitle>
            </CardHeader>

            <CardContent>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {fields.map(
                  renderField,
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>
                📋 Preview
              </CardTitle>
            </CardHeader>

            <CardContent>
              <div className="overflow-hidden rounded-lg border">
                {fields.map(
                  (field) => (
                    <div
                      key={field.name}
                      className="grid grid-cols-2 border-b p-3 last:border-b-0"
                    >
                      <span className="font-medium">
                        {field.label}
                      </span>

                      <span className="break-words">
                        {field.type ===
                        "select"
                          ? getOptionLabel(
                              field,
                              formData[
                                field.name
                              ],
                            )
                          : field.name ===
                                "embank" &&
                              !isMovement
                            ? "-"
                            : formData[
                                field.name
                              ] || "-"}
                      </span>
                    </div>
                  ),
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {pdfError && (
          <p className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-red-700">
            {pdfError}
          </p>
        )}

        <div className="mt-6 flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={goPrevious}
            disabled={
              currentTab === 0
            }
          >
            ← Previous
          </Button>

          {currentTab === 1 ? (
            <Button
              type="button"
              ref={nextButtonRef}
              onClick={handleSubmit}
              disabled={pdfLoading}
            >
              {pdfLoading
                ? "Generating PDF..."
                : "Submit & Open PDF"}
            </Button>
          ) : (
            <Button
              type="button"
              ref={nextButtonRef}
              onClick={goNext}
            >
              Preview
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
